import api from '../../../../services/api';
import { getApiResponse, handleApiError } from '../helpers/apiResponse';


export const getMembersList = async (params = {}) => {
  try {
    if (!params.knowledge_base_uuid) {
      throw new Error('knowledge_base_uuid is required');
    }

    const body = {
      knowledge_base_uuid: params.knowledge_base_uuid,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
      order_by: params.order_by ?? ['updated_at'],
      order_direction: params.order_direction ?? ['DESC'],
    };

    // Add search if provided
    if (params.search) {
      const allowedSearchFields = ['user_full_name', 'user_email', 'member_role'];
      if (params.search.field && allowedSearchFields.includes(params.search.field)) {
        if (params.search.keyword && params.search.keyword.trim() !== '') {
          body.search = {
            field: params.search.field,
            keyword: params.search.keyword.trim(),
          };
        }
      }
    }

    // Add filters if provided
    if (params.filters) {
      if (params.filters.member_role) {
        body.filters = {
          member_role: params.filters.member_role,
        };
      }
    }

    const response = await api.post('/v1/knowledgebase/member/list', body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while listing knowledge base members.'
    );

    // Ensure body is always an array for list endpoints
    return {
      body: Array.isArray(normalizedResponse.body) ? normalizedResponse.body : [],
      meta: normalizedResponse.meta || {},
      status_code: normalizedResponse.status_code,
    };

  } catch (apiError) {
    console.warn("API call failed, falling back to mock data:", apiError.message);

    // Fallback to mock data
    try {
      // Mock data según el contrato del endpoint (camelCase primero)
      // Roles disponibles: ADMIN, APPROVER, CONTRIBUTER
      const mockMembers = [
        {
          memberUuid: 'm-001',
          userUuid: 'u-001',
          userFullName: 'Alice Smith',
          userEmail: 'alice@example.com',
          userImgUrl: 'https://example.com/images/alice.jpg',
          memberRole: 'ADMIN',
          status: 'ACTIVE',
          updatedAt: '2024-06-15T12:30:00Z',
        },
        {
          memberUuid: 'm-002',
          userUuid: 'u-002',
          userFullName: 'Bob Johnson',
          userEmail: 'bob@example.com',
          userImgUrl: 'https://example.com/images/bob.jpg',
          memberRole: 'CONTRIBUTER',
          status: 'ACTIVE',
          updatedAt: '2024-06-14T10:20:00Z',
        },
        {
          memberUuid: 'm-003',
          userUuid: 'u-003',
          userFullName: 'Carol White',
          userEmail: 'carol@example.com',
          userImgUrl: 'https://example.com/images/carol.jpg',
          memberRole: 'APPROVER',
          status: 'ACTIVE',
          updatedAt: '2024-06-13T15:45:00Z',
        },
        {
          memberUuid: 'm-004',
          userUuid: 'u-004',
          userFullName: 'David Brown',
          userEmail: 'david@example.com',
          userImgUrl: 'https://example.com/images/david.jpg',
          memberRole: 'CONTRIBUTER',
          status: 'ACTIVE',
          updatedAt: '2024-06-12T09:10:00Z',
        },
        {
          memberUuid: 'm-005',
          userUuid: 'u-005',
          userFullName: 'Eva Garcia',
          userEmail: 'eva@example.com',
          userImgUrl: 'https://example.com/images/eva.jpg',
          memberRole: 'APPROVER',
          status: 'ACTIVE',
          updatedAt: '2024-06-11T14:25:00Z',
        },
      ];

      let filteredMembers = [...mockMembers];

      // Apply search filter if provided
      if (params.search && params.search.keyword) {
        const searchKeyword = params.search.keyword.toLowerCase().trim();
        const searchField = params.search.field || 'user_full_name';

        filteredMembers = filteredMembers.filter((member) => {
          // Programación defensiva: camelCase primero, snake_case como fallback
          const fullName = member.userFullName || member.user_full_name || '';
          const email = member.userEmail || member.user_email || '';
          const role = member.memberRole || member.member_role || '';

          if (searchField === 'user_full_name') {
            return fullName.toLowerCase().includes(searchKeyword);
          }
          if (searchField === 'user_email') {
            return email.toLowerCase().includes(searchKeyword);
          }
          if (searchField === 'member_role') {
            return role.toLowerCase().includes(searchKeyword);
          }
          return false;
        });
      }

      // Apply role filter if provided
      if (params.filters && params.filters.member_role) {
        filteredMembers = filteredMembers.filter((member) => {
          // Programación defensiva: camelCase primero, snake_case como fallback
          const memberRole = member.memberRole || member.member_role;
          return memberRole === params.filters.member_role;
        });
      }

      // Apply sorting
      const orderBy = params.order_by?.[0] || 'updated_at';
      const orderDirection = params.order_direction?.[0] || 'DESC';

      filteredMembers.sort((a, b) => {
        let aValue, bValue;

        switch (orderBy) {
          case 'updated_at':
            // Programación defensiva: camelCase primero, snake_case como fallback
            const aUpdated = a.updatedAt || a.updated_at;
            const bUpdated = b.updatedAt || b.updated_at;
            aValue = new Date(aUpdated).getTime();
            bValue = new Date(bUpdated).getTime();
            break;
          case 'user_full_name':
            // Programación defensiva: camelCase primero, snake_case como fallback
            const aName = a.userFullName || a.user_full_name || '';
            const bName = b.userFullName || b.user_full_name || '';
            aValue = aName.toLowerCase();
            bValue = bName.toLowerCase();
            break;
          case 'member_role':
            // Programación defensiva: camelCase primero, snake_case como fallback
            const aRole = a.memberRole || a.member_role || '';
            const bRole = b.memberRole || b.member_role || '';
            aValue = aRole.toLowerCase();
            bValue = bRole.toLowerCase();
            break;
          case 'user_email':
            // Programación defensiva: camelCase primero, snake_case como fallback
            const aEmail = a.userEmail || a.user_email || '';
            const bEmail = b.userEmail || b.user_email || '';
            aValue = aEmail.toLowerCase();
            bValue = bEmail.toLowerCase();
            break;
          default:
            // Programación defensiva: camelCase primero, snake_case como fallback
            const aDefaultUpdated = a.updatedAt || a.updated_at;
            const bDefaultUpdated = b.updatedAt || b.updated_at;
            aValue = new Date(aDefaultUpdated).getTime();
            bValue = new Date(bDefaultUpdated).getTime();
        }

        if (typeof aValue === 'string') {
          return orderDirection === 'ASC'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return orderDirection === 'ASC' ? aValue - bValue : bValue - aValue;
        }
      });

      // Apply pagination
      const page = params.page ?? 1;
      const pageSize = params.page_size ?? 20;
      const totalItems = filteredMembers.length;
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

      // Mock meta según contrato del API (camelCase en pagination)
      const mockMeta = {
        pagination: {
          page: page,
          page_size: pageSize,
          total_items: totalItems,
          total_pages: Math.ceil(totalItems / pageSize),
          has_next: endIndex < totalItems,
          has_prev: page > 1,
        },
      };

      return {
        body: paginatedMembers,
        meta: mockMeta,
        status_code: 200,
      };
    } catch (mockError) {
      // If mock also fails, throw the original API error
      console.error("Mock data also failed:", mockError.message);
      throw handleApiError(apiError, 'An error occurred while listing knowledge base members.');
    }
  }
};

/**
 * Add members to a knowledge base
 * 
 * @param {Object} params - Member data
 * @param {string} params.knowledge_base_uuid - Knowledge base UUID
 * @param {Array} params.members - Array of members to add
 * @param {string} params.members[].user_uuid - User UUID
 * @param {string} params.members[].member_role - Member role (ADMIN, CONTRIBUTOR, VIEWER)
 * @returns {Promise<Object>} Response with added members, status_code, and msg_code
 */
export const addMembers = async (params = {}) => {
  try {
    if (!params.knowledge_base_uuid) {
      throw new Error('knowledge_base_uuid is required');
    }
    if (!params.members || !Array.isArray(params.members) || params.members.length === 0) {
      throw new Error('members array is required and must not be empty');
    }

    const body = {
      knowledge_base_uuid: params.knowledge_base_uuid,
      members: params.members.map(member => ({
        user_uuid: member.user_uuid,
        member_role: member.member_role,
      })),
    };

    const response = await api.post('/v1/knowledgebase/member/add', body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while adding members to knowledge base.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
      msg_code: normalizedResponse.msg_code || [],
    };

  } catch (error) {
    // Use helper to normalize error handling
    throw handleApiError(error, 'An error occurred while adding members to knowledge base.');
  }
};

/**
 * Update a member's role in a knowledge base
 * 
 * @param {Object} params - Member update data
 * @param {string} params.knowledge_base_uuid - Knowledge base UUID
 * @param {string} params.member_uuid - Member UUID
 * @param {string} params.user_uuid - User UUID
 * @param {string} params.new_role - New role for the member (admin, contributor, viewer)
 * @returns {Promise<Object>} Response with updated member data, status_code, and msg_code
 */
export const updateMember = async (params = {}) => {
  try {
    if (!params.knowledge_base_uuid) {
      throw new Error('knowledge_base_uuid is required');
    }
    if (!params.member_uuid) {
      throw new Error('member_uuid is required');
    }
    if (!params.user_uuid) {
      throw new Error('user_uuid is required');
    }
    if (!params.new_role) {
      throw new Error('new_role is required');
    }

    const body = {
      knowledge_base_uuid: params.knowledge_base_uuid,
      member_uuid: params.member_uuid,
      user_uuid: params.user_uuid,
      new_role: params.new_role,
    };

    const response = await api.post('/v1/knowledgebase/member/update', body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while updating member role.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
      msg_code: normalizedResponse.msg_code || [],
    };

  } catch (error) {
    // Use helper to normalize error handling
    throw handleApiError(error, 'An error occurred while updating member role.');
  }
};

/**
 * Remove a member from a knowledge base
 * 
 * @param {Object} params - Member removal data
 * @param {string} params.knowledge_base_uuid - Knowledge base UUID
 * @param {string} params.member_uuid - Member UUID
 * @param {string} params.user_uuid - User UUID
 * @returns {Promise<Object>} Response with removed member data, status_code, and msg_code
 */
export const removeMember = async (params = {}) => {
  try {
    if (!params.knowledge_base_uuid) {
      throw new Error('knowledge_base_uuid is required');
    }
    if (!params.member_uuid) {
      throw new Error('member_uuid is required');
    }
    if (!params.user_uuid) {
      throw new Error('user_uuid is required');
    }

    const body = {
      knowledge_base_uuid: params.knowledge_base_uuid,
      member_uuid: params.member_uuid,
      user_uuid: params.user_uuid,
    };

    const response = await api.post('/v1/knowledgebase/member/remove', body);
    const responseData = response.data;

    // Use helper to normalize response and handle errors
    const normalizedResponse = getApiResponse(
      responseData,
      response,
      'An error occurred while removing member from knowledge base.'
    );

    return {
      body: normalizedResponse.body || {},
      meta: normalizedResponse.meta || null,
      status_code: normalizedResponse.status_code,
      msg_code: normalizedResponse.msg_code || [],
    };

  } catch (error) {
    // Use helper to normalize error handling
    throw handleApiError(error, 'An error occurred while removing member from knowledge base.');
  }
};

